import { setup, assign, fromPromise, emit } from "xstate";
import { createActorContext } from "@xstate/react";
import { ExifWorkerPool } from "../workers/worker-pool";
import {
  MachineContext,
  FileProcessingEvent,
  ProcessedImage,
  isProcessedFileUploaded,
} from "./types";
import { generateUniqueFileIdentifier } from "./uploadMachine";
import { createUploads, deleteUploads } from "./uploadMachine/services";
import { createBrowserInspector } from "@statelyai/inspect";

function computeMissingFlag(upload: ProcessedImage): boolean {
  if (isProcessedFileUploaded(upload)) {
    return false;
  }

  return !(upload.file instanceof File);
}

interface ImagesContext extends MachineContext<ProcessedImage> {
  processing: string[];
  speed: number;
  startedAt: number | null;
  hover: Record<string, boolean>;
  selected: string[];
}

type ImagesEvent = FileProcessingEvent;

const SIZE_128KB = 128 * 1024;

let workerPool: ExifWorkerPool | null = null;

function getWorkerPool(): ExifWorkerPool {
  if (!workerPool) {
    workerPool = new ExifWorkerPool();
  }
  return workerPool;
}

function runExifWorker(buffer: ArrayBuffer) {
  return getWorkerPool().process(buffer);
}

export function cleanupImageWorkerPool() {
  if (workerPool) {
    workerPool.terminate();
    workerPool = null;
  }
}

async function identifyImages({
  input,
}: {
  input: { files: File[]; context?: ImagesContext };
}) {
  const output: ProcessedImage[] = [];
  const context = input.context;

  for (const file of input.files) {
    const extension = file.name.toLowerCase().split(".").pop();

    if (extension === "jpg" || extension === "jpeg") {
      const id = generateUniqueFileIdentifier(file).name;

      const existingUpload = context.uploads.find(
        (upload) => upload.id === id && isProcessedFileUploaded(upload)
      );

      if (!existingUpload) {
        const newUpload = {
          id,
          file,
          progress: 0,
        };
        output.push({
          ...newUpload,
          missing: computeMissingFlag(newUpload),
        });
      }
    }
  }

  return { images: output };
}

async function processImages({
  input,
}: {
  input: { activeIds: string[]; uploads: ProcessedImage[] };
}) {
  const { activeIds, uploads } = input;

  const results = await Promise.allSettled(
    activeIds.map(async (id) => {
      const upload = uploads.find((u) => u.id === id);

      if (!upload) throw new Error(`Upload ${id} not found`);

      const result = await processImage({
        input: { image: upload },
      });

      return result;
    })
  );

  return results.map((result, index) => ({
    id: activeIds[index],
    result:
      result.status === "fulfilled"
        ? result.value
        : {
            id: activeIds[index],
            error: result.reason?.message || "Processing failed",
          },
  }));
}

async function processImage({ input }: { input: { image: ProcessedImage } }) {
  const { image } = input;

  if (image.file instanceof File) {
    try {
      const exif = await runExifWorker(
        await image.file.slice(0, SIZE_128KB).arrayBuffer()
      );

      return {
        id: image.id,
        data: { exif },
        progress: 1,
      };
    } catch (error) {
      return {
        id: image.id,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  } else {
    return {
      id: image.id,
      progress: 1,
    };
  }
}

async function removeImage({ input }: { input: { id: string } }) {
  const { id } = input;

  return {
    id,
  };
}

async function createImageUploads({
  input,
}: {
  input: { images: ProcessedImage[]; surveyId: number; orderId: number };
}) {
  const { images, surveyId, orderId } = input;

  if (images.length === 0) {
    return { created: [] };
  }

  const created = await createUploads({
    input: images.map((image) => ({
      key: generateUniqueFileIdentifier(image.file).name,
      fileName: image.file.name,
      fileSize: image.file.size,
      fileType: image.file.type,
      fileModifiedAt: image.file.lastModified,
      fileExtension: image.file.name.split(".").pop()?.toLowerCase() || "",
      fileMetadata: image.data || {},
      surveyId,
      orderId,
    })),
  });

  return { created };
}

async function deleteImageUploads({
  input,
}: {
  input: { keys: string[]; surveyId: number; orderId: number };
}) {
  const { keys, surveyId, orderId } = input;

  if (keys.length === 0) {
    return { deleted: [] };
  }

  const deleted = await deleteUploads({ input: { keys, surveyId, orderId } });

  return { deleted };
}

export const createImagesMachine = setup({
  types: {} as {
    context: ImagesContext;
    events: ImagesEvent;
    input: { existingUploads?: any[]; surveyId?: number; orderId?: number };
    emitted: { type: "focusOnImages" };
  },
  actors: {
    identifyImages: fromPromise(identifyImages),
    processImage: fromPromise(processImage),
    processImages: fromPromise(processImages),
    createImageUploads: fromPromise(createImageUploads),
    deleteImageUploads: fromPromise(deleteImageUploads),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFsCGA7VMCSaawDoBLCAGzAGIBBAERoH0AxbAGQFEBlAbQAYBdRKAAOAe1hEALkRHpBIAB6IAjEoDMADgIBOHVvXqeAFh6rVAdgA0IAJ6IATOoCsBM+o2Othu0q12tSxwBfQKs0TBw8OGIIMHQpADNrCggZMGJ0ADcRAGs0kliE61wsOF4BJBBRcSkZOUUEAFo7HmclHgCANjtHK1sEDp47bUNfJS7VTzUtYNCMEuL8aIKiROTU9KzcpbiVosjYLiVy4TFJaVkK+pUHbV19IxNzXsRTDoIHdw6nMy1zGZAwvN9ttChQwAAncEicEEISkVASeLQ5Ag3YLUr8ORVM61S7KNSaXR6AzGUyWGz2DTvNzqDqmQxKVxaIIhAFzCIlQhCKEAYzg4nQUAIPIAFmAedkKGUsacahdQPUzDwtAR1GZHDxNT8-B0zOS+t03kqWjw6UpDGrDCzZuEwOiubz+URBcKxRKpUcZdVznVlCYVUS1XTHI5XHZnggNG9HuoRrqOv5VHZ-oCOYtuSI+bABULYBJUOCJFLMRVsXLfQh1UMzKpDKozbqxj0KQgAjxbjp1AEAmYlHYuin2XbgRmsznYY7s2t0HlMjk0qnh5yJ5mnS7R-yEM6sjyEecytLS7KfXiEP23o5OqTzW46aoI-XVAQk12mypHPWa4PbfaV2PnUKG5ThCUIwnCCJIuCKKLr+QHjnBW5zrucoHiWJzeriCr2B0F5XiYN6PveLZ2Eq7wjFoPwhqG6h2HW35AsuPIiMgcJgBIlAAEqcGwAAqh7oTi8oKIgPARjwwSsugIgxPAFQwfsXqCRWTQfgQbSdN0EYNGqBDWmyP7AiQ5CKeWp5tBMHZ6GYwbUeGxGGG8Wg8PoOGqJqoyePRaZRPkOyJCZJ5YQgFERvoT6GFaSY+FaYxdl5S7ppOOYBZhwmRloEZ0gQ5F6N2oZ9gOrLycucEAa64rZClQn1B0faqnWZgMjRFodKGD56qqjaOLRjV2N0yZFUOsFJWVeYFhIVUVrGzjMqoKhKjR+iuBG3iaDG3iOAmWgdHWenFYlq7ZmVcGTaeaoPlSOjeF0XgWv2jXxb+TEseQ7GnUFfYtO8ShqhMtLWX1HQPrG1JRuanjNI4sYSYEQA */
  id: "manageImages",
  initial: "idle",
  context: ({ input }) =>
    ({
      uploads:
        input?.existingUploads?.map((upload) => {
          const processedUpload = {
            id: upload.key,
            file: {
              size: upload.file_size,
              name: upload.file_name,
              type: upload.file_type,
              lastModified: upload.file_modified_at,
            },
            data: upload.file_metadata,
            status: upload.status,
          };
          return {
            ...processedUpload,
            missing: computeMissingFlag(processedUpload),
          };
        }) || [],
      active: [],
      processing: [],
      removing: null,
      speed: 0,
      startedAt: null,
      hover: {},
      selected: [],
      surveyId: input?.surveyId ?? 0,
      orderId: input?.orderId ?? 0,
    } as ImagesContext),
  states: {
    idle: {
      entry: () => {
        cleanupImageWorkerPool();
      },
      on: {
        ADD_FILES: {
          target: "identify",
          guard: ({ event }: { event: { files: File[] } }) =>
            event.files.length > 0,
        },
        REMOVE_FILE: {
          target: "remove",
          actions: assign({
            removing: ({ context, event }) => event.id,
          }),
        },
        REMOVE_ALL: {
          target: "clear",
        },
        REMOVE_SELECTED: {
          target: "removeSelected",
        },
        REMOVE_MISSING: {
          target: "removeMissing",
        },
        MARKER_OVER: {
          actions: assign({
            hover: ({ context, event }) => ({
              ...context.hover,
              [event.id]: true,
            }),
          }),
        },
        MARKER_OUT: {
          actions: assign({
            hover: ({ context, event }) => ({
              ...context.hover,
              [event.id]: false,
            }),
          }),
        },
        MARKER_SELECT: {
          actions: assign({
            selected: ({ context, event }) => [...context.selected, event.id],
          }),
        },
        MARKER_DESELECT: {
          actions: assign({
            selected: ({ context, event }) =>
              context.selected.filter((id) => id !== event.id),
          }),
        },
        MARKER_DESELECT_ALL: {
          actions: assign({
            selected: [],
          }),
        },
        MARKER_RESET: {
          actions: assign({
            hover: {},
            selected: [],
          }),
        },
      },
    },
    remove: {
      initial: "confirm",
      states: {
        confirm: {
          on: {
            CONFIRM_REMOVE: { target: "removing" },
            CANCEL_REMOVE: { target: "#manageImages.idle" },
          },
        },
        removing: {
          invoke: {
            id: "deleteImageUploads",
            src: "deleteImageUploads",
            input: ({ event, context }) => {
              if (event.type === "CONFIRM_REMOVE") {
                return {
                  keys: [context.removing],
                  surveyId: context.surveyId,
                  orderId: context.orderId,
                };
              }

              throw new Error("Invalid event type");
            },
            onDone: {
              target: "#manageImages.idle",
              actions: [
                assign({
                  removing: null,
                  uploads: ({ context }) =>
                    context.uploads.filter((u) => u.id !== context.removing),
                  selected: ({ context }) =>
                    context.selected.filter((id) => id !== context.removing),
                  hover: ({ context }) => {
                    const { [context.removing]: _, ...rest } = context.hover;
                    return rest;
                  },
                }),
                emit({ type: "focusOnImages" }),
              ],
            },
          },
        },
      },
    },
    clear: {
      initial: "confirm",
      states: {
        confirm: {
          on: {
            CONFIRM_REMOVE_ALL: { target: "removing" },
            CANCEL_REMOVE: { target: "#manageImages.idle" },
          },
        },
        removing: {
          invoke: {
            id: "deleteAllImageUploads",
            src: "deleteImageUploads",
            input: ({ context }) => ({
              keys: context.uploads.map((upload) => upload.id),
              surveyId: context.surveyId,
              orderId: context.orderId,
            }),
            onDone: {
              target: "#manageImages.idle",
              actions: [
                assign({
                  uploads: [],
                  removing: null,
                  hover: {},
                  selected: [],
                }),
                emit({ type: "focusOnImages" }),
              ],
            },
            onError: {
              target: "#manageImages.idle",
            },
          },
        },
      },
    },
    removeSelected: {
      initial: "confirm",
      states: {
        confirm: {
          on: {
            CONFIRM_REMOVE_SELECTED: { target: "removing" },
            CANCEL_REMOVE: { target: "#manageImages.idle" },
          },
        },
        removing: {
          invoke: {
            id: "deleteSelectedImageUploads",
            src: "deleteImageUploads",
            input: ({ context }) => ({
              keys: context.selected,
              surveyId: context.surveyId,
              orderId: context.orderId,
            }),
            onDone: {
              target: "#manageImages.idle",
              actions: [
                assign({
                  uploads: ({ context }) =>
                    context.uploads.filter(
                      (upload) => !context.selected.includes(upload.id)
                    ),
                  hover: ({ context }) => {
                    const newHover = { ...context.hover };
                    context.selected.forEach((id) => delete newHover[id]);
                    return newHover;
                  },
                  selected: [],
                  removing: null,
                }),
                emit({ type: "focusOnImages" }),
              ],
            },
            onError: {
              target: "#manageImages.idle",
            },
          },
        },
      },
    },
    removeMissing: {
      initial: "confirm",
      states: {
        confirm: {
          on: {
            CONFIRM_REMOVE_MISSING: { target: "removing" },
            CANCEL_REMOVE: { target: "#manageImages.idle" },
          },
        },
        removing: {
          invoke: {
            id: "deleteMissingImageUploads",
            src: "deleteImageUploads",
            input: ({ context }) => ({
              keys: context.uploads
                .filter((upload) => upload.missing)
                .map((upload) => upload.id),
              surveyId: context.surveyId,
              orderId: context.orderId,
            }),
            onDone: {
              target: "#manageImages.idle",
              actions: [
                assign({
                  uploads: ({ context }) =>
                    context.uploads.filter((upload) => !upload.missing),
                  hover: {},
                  selected: [],
                  removing: null,
                }),
                emit({ type: "focusOnImages" }),
              ],
            },
            onError: {
              target: "#manageImages.idle",
            },
          },
        },
      },
    },
    identify: {
      invoke: {
        id: "identifyImages",
        src: "identifyImages",
        input: ({ event, context }) => {
          if (event.type === "ADD_FILES") {
            return { files: event.files, context };
          }
          throw new Error("Invalid event type");
        },
        onDone: [
          {
            guard: ({ event }) => event.output.images.length > 0,
            target: "processing",
            actions: assign({
              uploads: ({ context, event }) => {
                const outputMap = new Map(
                  event.output.images.map((u) => [u.id, u])
                );

                return [
                  ...context.uploads.map((u) => {
                    const updated = outputMap.get(u.id) ?? u;
                    return {
                      ...updated,
                      missing: computeMissingFlag(updated),
                    };
                  }),
                  ...event.output.images.filter(
                    (u) =>
                      !context.uploads.some((existing) => existing.id === u.id)
                  ),
                ];
              },
              processing: ({ event }) =>
                event.output.images.map((img: ProcessedImage) => img.id),
            }),
          },
          {
            target: "idle",
          },
        ],
        onError: {
          target: "idle",
        },
      },
    },
    processing: {
      initial: "check",
      states: {
        check: {
          always: [
            {
              guard: ({ context }) => {
                return (
                  context.uploads.filter(
                    (upload) => !upload.data && !upload.error
                  ).length > 0
                );
              },
              target: "start",
            },
            { target: "#manageImages.creating" },
          ],
        },
        start: {
          entry: assign({
            active: ({ context }) =>
              context.uploads
                .filter((upload) => !upload.data && !upload.error)
                .map((upload) => upload.id),
            startedAt: Date.now(),
          }),
          always: {
            target: "process",
          },
        },
        process: {
          invoke: {
            src: "processImages",
            input: ({ context }) => ({
              activeIds: context.active as string[],
              uploads: context.uploads,
            }),
            onDone: {
              target: "check",
              actions: assign({
                uploads: ({ context, event }) =>
                  context.uploads.map((upload) => {
                    const output = (
                      event.output as Array<{
                        id: string;
                        result?: any;
                        error?: string;
                      }>
                    ).find((r) => r.id === upload.id);

                    if (output?.result) {
                      const updated = {
                        ...upload,
                        data: output.result.data,
                        progress: output.result.progress,
                        error: output.result.error,
                      };
                      return {
                        ...updated,
                        missing: computeMissingFlag(updated),
                      };
                    }

                    return upload;
                  }),
                active: [],
                speed: ({ context, event }) => {
                  if (!context.startedAt) return 0;

                  const processedUploads = event.output.filter(
                    (output) => output.result && !output.result.error
                  );
                  const totalBytes = processedUploads.reduce(
                    (total, output) => {
                      const upload = context.uploads.find(
                        (u) => u.id === output.id
                      );
                      return total + (upload?.file.size || 0);
                    },
                    0
                  );

                  const elapsedSeconds =
                    (Date.now() - context.startedAt) / 1000;
                  return elapsedSeconds > 0 ? totalBytes / elapsedSeconds : 0;
                },
              }),
            },
            onError: {
              target: "check",
              actions: assign({
                uploads: ({ context }) =>
                  context.uploads.map((upload) => {
                    if ((context.active as string[]).includes(upload.id)) {
                      return {
                        ...upload,
                        error: "Processing failed",
                      };
                    }
                    return upload;
                  }),
                active: [],
                speed: ({ context }) => {
                  if (!context.startedAt) return 0;

                  const successfulUploads = context.uploads.filter(
                    (upload) => upload.data && !upload.error
                  );

                  const totalBytes = successfulUploads.reduce(
                    (total, upload) => total + upload.file.size,
                    0
                  );

                  const elapsedSeconds =
                    (Date.now() - context.startedAt) / 1000;
                  return elapsedSeconds > 0 ? totalBytes / elapsedSeconds : 0;
                },
              }),
            },
          },
        },
      },
    },
    creating: {
      invoke: {
        id: "createImageUploads",
        src: "createImageUploads",
        input: ({ context }: { context: ImagesContext }) => {
          return {
            images: context.uploads.filter((upload) =>
              context.processing.includes(upload.id)
            ),
            surveyId: context.surveyId,
            orderId: context.orderId,
          };
        },
        onDone: {
          target: "idle",
          actions: emit({ type: "focusOnImages" }),
        },
        onError: {
          target: "processing",
        },
      },
    },
  },
});

export const ImagesMachineContext = createActorContext(createImagesMachine, {});
